import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/true-fsd/shared/utils/css'

const DialogRoot = DialogPrimitive.Root
export type DialogProps = DialogPrimitive.DialogProps

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 h-full bg-black opacity-30',
            className
        )}
        {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/// Content with a close button
const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        closeClassName?: string
    } & { iconSrc?: string; overlayStyle?: React.CSSProperties; overlayClassName?: string }
>(
    (
        {
            className,
            iconSrc,
            overlayStyle,
            overlayClassName,
            closeClassName,
            children,
            ...props
        },
        ref
    ) => (
        <DialogPortal>
            <DialogOverlay style={overlayStyle} className={overlayClassName} />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg',
                    className
                )}
                {...props}
            >
                {children}
                <DialogPrimitive.Close
                    autoFocus={false}
                    className={cn(
                        'ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none',
                        closeClassName
                    )}
                >
                    {iconSrc ? (
                        <img src={iconSrc} />
                    ) : (
                        <X className="h-4 w-4" />
                    )}
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            </DialogPrimitive.Content>
        </DialogPortal>
    )
)
DialogContent.displayName = DialogPrimitive.Content.displayName
export type DialogContentProps = React.ComponentPropsWithoutRef<
    typeof DialogContent
>

/// Content without a close button
const DialogSimpleContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        closeClassName?: string
        outerChildren?: React.ReactNode
    } & { iconSrc?: string; overlayStyle?: React.CSSProperties }
>(
    (
        { className, iconSrc, overlayStyle, children, outerChildren, ...props },
        ref
    ) => (
        <DialogPortal>
            <DialogOverlay style={overlayStyle} />

            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200',
                    className
                )}
                {...props}
            >
                {children}
            </DialogPrimitive.Content>
            {outerChildren}
        </DialogPortal>
    )
)
DialogSimpleContent.displayName = DialogPrimitive.Content.displayName
export type DialogSimpleContentProps = React.ComponentPropsWithoutRef<
    typeof DialogSimpleContent
>

const DialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-col space-y-1.5 text-center sm:text-left',
            className
        )}
        {...props}
    />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex', className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn(
            'text-lg font-semibold leading-none tracking-tight',
            className
        )}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('text-muted-foreground text-sm', className)}
        {...props}
    />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

// const Dialog = DialogPrimitive.Root
const Dialog = ({ children, ...props }: DialogPrimitive.DialogProps) => {
    return (
        <DialogPrimitive.Root {...props}>
            {props.open && !props.modal ? (
                <DialogPortal>
                    <div className="data-[state=open]:animate-in data-[state=closed]:animate-out fixed inset-0 z-50 bg-black opacity-30 ..." />
                </DialogPortal>
            ) : null}
            {children}
        </DialogPrimitive.Root>
    )
}

export {
    DialogRoot,
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
    DialogSimpleContent,
}
